import { message } from 'antd';
import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Loading from '@/components/common/Loading';
import { VideoPlayerSurface } from '@/components/common/VideoPlayer';
import { imageToImageModelConfig } from '@/components/ModelControl/config/imageToImage';
import { imageToVideoModelConfig } from '@/components/ModelControl/config/imageToVideo';
import { downloadStreamFile } from '@/utils/fileUtil';
import { t } from '@/utils/helper/intl';

import CommonModal from '../index';

interface ResultPreviewModalOptions {
  mediaType: 'video' | 'image';
  src: string;
  cover?: string;
  title?: string;
  prompt?: string;
  promptImage?: string | string[];
  modelName?: string;
  createdAt?: number;
  onContinueGenerating?: () => void;
  onClose?: () => void;
  metaItems?: string[];
  detailSections?: Array<{
    title?: string;
    items: Array<{
      label: string;
      value?: string;
      images?: string[];
    }>;
  }>;
}

const ALL_MODEL_CONFIGS = [...imageToVideoModelConfig, ...imageToImageModelConfig];
const normalizeModelName = (name?: string) =>
  (name || '')
    .toLowerCase()
    .replace(/[\s._-]/g, '')
    .replace(/version/g, 'v')
    .replace(/v(?=\d)/g, '');

const ResultPreviewContent = (
  props: ResultPreviewModalOptions & { onClose?: () => void },
) => {
  const [imageLoading, setImageLoading] = useState(props.mediaType === 'image');

  useEffect(() => {
    setImageLoading(props.mediaType === 'image');
  }, [props.mediaType, props.src, props.cover]);

  const handleDownload = useCallback(async () => {
    const targetUrl = props.src || props.cover;
    if (!targetUrl) {
      message.error(t('Download-Error'));
      return;
    }

    try {
      downloadStreamFile({
        fileUrl: targetUrl,
        fileName: props.title || 'result',
      });
    } catch {
      message.error(t('Download-Error'));
    }
  }, [props.cover, props.src, props.title]);

  const promptImages = useMemo(() => {
    if (!props.promptImage) return [];
    return Array.isArray(props.promptImage) ? props.promptImage : [props.promptImage];
  }, [props.promptImage]);
  const promptText = props.prompt?.trim() || '';
  const detailSections = useMemo(
    () =>
      (props.detailSections || [])
        .map((section) => ({
          ...section,
          items: (section.items || []).filter(
            (item) =>
              Boolean(item?.value?.trim()) ||
              Boolean(item?.images?.filter(Boolean).length),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [props.detailSections],
  );
  const hasDetailSection = detailSections.length > 0;
  const hasMetaSection = Boolean(props.metaItems?.filter(Boolean).length);
  const hasPromptSection =
    !hasMetaSection &&
    !hasDetailSection &&
    Boolean(promptText || promptImages.length > 0 || props.modelName);
  const formattedTime = useMemo(() => {
    if (!props.createdAt) return '';
    const date = new Date(props.createdAt);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mi = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
  }, [props.createdAt]);
  const modelLogoNode = useMemo(() => {
    const modelName = normalizeModelName(props.modelName);
    const matchedConfig = ALL_MODEL_CONFIGS.find((m) => {
      const candidate = normalizeModelName(m.modelName);
      return (
        candidate === modelName ||
        candidate.includes(modelName) ||
        modelName.includes(candidate)
      );
    });

    if (matchedConfig?.iconUrl) {
      return (
        <div className="relative w-[20px] h-[20px]  overflow-hidden shrink-0">
          <Image
            src={matchedConfig.iconUrl}
            alt={matchedConfig.modelName}
            fill
            unoptimized
            className="object-cover"
          />
        </div>
      );
    }

    return (
      <span className="w-[16px] h-[16px] rounded-full bg-[#F3F4F6] border border-[#E6E6E6] text-[10px] font-[700] leading-[16px] text-center text-[#666] shrink-0">
        M
      </span>
    );
  }, [props.modelName]);

  return (
    <div className="w-full">
      <div className="w-full h-[676px] rounded-[12px] overflow-hidden bg-[#fff] border border-[#E7E7E7] flex">
        <div
          className={`${hasPromptSection || hasMetaSection || hasDetailSection ? 'flex-1 shrink-0' : 'w-full'} h-full p-[24px]`}
        >
          <div className="relative mx-auto flex h-[628px] max-h-full max-w-full flex-1 items-center justify-center overflow-hidden rounded-[8px] bg-black">
            <div
              className="p-[4px] gap-[4px] flex items-center justify-center absolute z-10 right-[10px] top-[10px] bg-[#fff] rounded-[8px] border border-[#E7E7E7] min-w-[60px] cursor-pointer"
              onClick={handleDownload}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
              >
                <path
                  d="M9.00311 12.7123V2.25"
                  stroke="#333333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.5 8.25L9 12.75L4.5 8.25"
                  stroke="#333333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.5 15.75H4.5"
                  stroke="#333333"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-[#111111CC] text-[12px]">
                {t('merror.operate.download')}
              </span>
            </div>
            {props.mediaType === 'video' ? (
              <div className="w-full h-full relative">
                <VideoPlayerSurface
                  src={props.src}
                  poster={props.cover}
                  inline
                  fit="contain"
                  className="w-full h-full relative"
                />
              </div>
            ) : (
              <>
                {imageLoading && (
                  <div className="absolute inset-0 z-[2] flex items-center justify-center bg-[#F6F7FB]">
                    <Loading size={36} />
                  </div>
                )}
                <img
                  src={props.src || props.cover || ''}
                  alt={props.title || 'preview'}
                  className="h-auto max-h-full w-auto max-w-full object-contain rounded-[8px]"
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
              </>
            )}
          </div>
        </div>

        {hasPromptSection && (
          <div className="shrink-0 h-full bg-[#fff] border-l border-[#E7E7E7] p-[24px] flex flex-col gap-[16px] max-w-[400px]">
            <div className="w-full shrink-0">
              {!!promptText && (
                <>
                  {/* <div className="text-[12px] font-[500] text-[#11111199] leading-[16px] mb-[8px]">
                    {t('68a75660cc45dbee091e3fc745e7dc00')}:
                  </div> */}
                  <div className="text-[12px] leading-[16px] text-[#111] whitespace-pre-wrap break-words mb-[8px]">
                    {promptText}
                  </div>
                </>
              )}
              <div className="shrink-0 w-fit h-[23px] flex items-center text-[14px] text-[#666] leading-[16px] min-w-0">
                {promptImages[0] ? (
                  <>
                    <div className="flex items-center w-fit shrink-0 min-w-0">
                      <div className="relative w-[20px] h-[24px] rounded-full overflow-hidden bg-[#F3F4F6] shrink-0">
                        <Image
                          src={promptImages[0]}
                          alt="reference"
                          fill
                          unoptimized
                          className="object-cover"
                        />
                      </div>
                      <span className="ml-[4px] truncate text-[12px]">
                        {t('fc.74864f8a.seedance20.referenceImages')}
                      </span>
                    </div>
                    <div className="w-[1px] h-[16px] bg-[#D9D9D9] mx-[8px] shrink-0" />
                  </>
                ) : null}
                <div className="flex items-center gap-[4px]  min-w-0 shrink-0 text-[12px]">
                  {modelLogoNode}
                  <span className="truncate">{props.modelName || '-'}</span>
                </div>
                <div className="w-[1px] h-[16px] bg-[#D9D9D9] mx-[8px] shrink-0" />
                <span className="w-fit shrink-0 text-[12px] leading-[16px] text-[#666] text-right">
                  {formattedTime || '-'}
                </span>
              </div>
            </div>

            <div className="pb-[4px]">
              {props.onContinueGenerating ? (
                <button
                  type="button"
                  onClick={() => {
                    props.onContinueGenerating?.();
                    props.onClose?.();
                  }}
                  className="p-[8px] rounded-[10px] border border-[#D9D9D9] bg-white px-[8px] text-[14px] leading-[14px] text-[#4A4A4A] whitespace-nowrap flex items-center justify-center gap-[4px] hover:bg-[#F7F7F7] transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    style={{ flexShrink: 0 }}
                  >
                    <path
                      d="M7.5 12H3.75V15.75M10.5 6H14.25V2.25M3.43726 6.75255C3.85777 5.71175 4.56183 4.80978 5.46941 4.14917C6.377 3.48856 7.45264 3.09564 8.57232 3.01538C9.69199 2.93512 10.8111 3.17055 11.8036 3.69496C12.7961 4.21937 13.6209 5.0119 14.1856 5.98209M14.5632 11.2478C14.1426 12.2886 13.4386 13.1906 12.531 13.8512C11.6234 14.5118 10.5489 14.9042 9.4292 14.9845C8.30953 15.0647 7.18955 14.8293 6.19702 14.3049C5.2045 13.7805 4.37912 12.9881 3.81445 12.0179"
                      stroke="#111111"
                      strokeOpacity="0.8"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{t('fc.generateAgain')}</span>
                </button>
              ) : null}
            </div>
          </div>
        )}

        {hasMetaSection && (
          <div className="shrink-0 h-full bg-[#fff] border-l border-[#E7E7E7] p-[24px] flex flex-col gap-[16px] max-w-[400px]">
            <div className="w-full shrink-0">
              <div className="shrink-0 w-fit h-[23px] flex items-center text-[14px] text-[#666] leading-[16px] min-w-0">
                {props.metaItems?.filter(Boolean).map((item, index, arr) => (
                  <React.Fragment key={`${item}-${index}`}>
                    <span className="w-fit shrink-0 text-[12px] leading-[16px] text-[#666] text-right">
                      {item}
                    </span>
                    {index < arr.length - 1 ? (
                      <div className="w-[1px] h-[16px] bg-[#D9D9D9] mx-[8px] shrink-0" />
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}

        {hasDetailSection && (
          <div className="shrink-0 h-full max-w-[400px] border-l border-[#E7E7E7] bg-[#fff] p-[24px]">
            <div className="flex h-full flex-col gap-[16px]">
              <div className="scroll-overlay flex-1 overflow-y-auto pr-[6px]">
                <div className="flex flex-col gap-[20px]">
                  {detailSections.map((section, sectionIndex) => (
                    <div key={`${section.title || 'section'}-${sectionIndex}`}>
                      {section.title ? (
                        <div className="mb-[10px] text-[12px] font-[500] leading-[16px] text-[#11111199]">
                          {section.title}
                        </div>
                      ) : null}
                      <div className="flex flex-col gap-[12px]">
                        {section.items.map((item, itemIndex) => (
                          <div
                            key={`${item.label}-${itemIndex}`}
                            className="flex flex-col gap-[6px]"
                          >
                            <div className="text-[12px] font-[500] leading-[16px] text-[#11111199]">
                              {item.label}
                            </div>
                            {item.value ? (
                              <div className="break-words whitespace-pre-wrap text-[13px] leading-[20px] text-[#111111]">
                                {item.value}
                              </div>
                            ) : null}
                            {item.images?.filter(Boolean).length ? (
                              <div className="flex flex-wrap gap-[8px]">
                                {item.images.filter(Boolean).map((image, imageIndex) => (
                                  <div
                                    key={`${image}-${imageIndex}`}
                                    className="relative h-[76px] w-[76px] overflow-hidden rounded-[10px] border border-[#ECECF3] bg-[#F6F7FB]"
                                  >
                                    <Image
                                      src={image}
                                      alt={item.label}
                                      fill
                                      unoptimized
                                      className="object-cover"
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pb-[4px]">
                {props.onContinueGenerating ? (
                  <button
                    type="button"
                    onClick={() => {
                      props.onContinueGenerating?.();
                      props.onClose?.();
                    }}
                    className="flex items-center justify-center gap-[4px] whitespace-nowrap rounded-[10px] border border-[#D9D9D9] bg-white px-[8px] py-[8px] text-[14px] leading-[14px] text-[#4A4A4A] transition-colors hover:bg-[#F7F7F7]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      style={{ flexShrink: 0 }}
                    >
                      <path
                        d="M7.5 12H3.75V15.75M10.5 6H14.25V2.25M3.43726 6.75255C3.85777 5.71175 4.56183 4.80978 5.46941 4.14917C6.377 3.48856 7.45264 3.09564 8.57232 3.01538C9.69199 2.93512 10.8111 3.17055 11.8036 3.69496C12.7961 4.21937 13.6209 5.0119 14.1856 5.98209M14.5632 11.2478C14.1426 12.2886 13.4386 13.1906 12.531 13.8512C11.6234 14.5118 10.5489 14.9042 9.4292 14.9845C8.30953 15.0647 7.18955 14.8293 6.19702 14.3049C5.2045 13.7805 4.37912 12.9881 3.81445 12.0179"
                        stroke="#111111"
                        strokeOpacity="0.8"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>{t('fc.generateAgain')}</span>
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export function resultPreviewModalShow(options: ResultPreviewModalOptions) {
  CommonModal.show(() => ({
    width: 1200,
    title: options.title,
    footer: false,
    content: (close) => (
      <ResultPreviewContent
        {...options}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
